import type { AppShell } from "@/App"

// Tipos das respostas REST da app (functions/api/**). Espelham o servidor a
// olho: as colunas NUMERIC chegam como texto, por isso os números são
// `number | string` e passam por `n()` antes de contas.

export type Num = number | string | null

export interface CarrierOption {
  carrier_id: string
  carrier_code: string
  carrier_name: string
  service_id: string
  service_name: string
  cost: number | null
  pickup: string
  arrival: string | null
  transit_days: number | null
  ok: boolean
  why: string | null
  missed_cutoff: boolean
}

export type OrderStatus = "ready" | "own_fleet" | "carrier" | "blocked" | "shipped" | "delivered" | "failed" | "cancelled"
export type BlockReason = "geo" | "weight" | "nosolution"
export type GeoPrecision = "cp7" | "cp4" | "address" | "manual" | "none"

export interface Order {
  id: string
  source_ref: string
  status: OrderStatus
  ready_at: string | null
  due_date: string | null
  carrier_deadline: string | null
  time_window_start: string | null
  time_window_end: string | null
  time_window_source: "ia" | "manual" | null
  time_window_confirmed: boolean
  delivery_note: string | null
  delivery_instructions: string[] | null
  call_before_min: number | null
  customer_name: string | null
  customer_phone: string | null
  address: string | null
  postal_code: string | null
  locality: string | null
  /** Concelho (da tabela de códigos postais). */
  municipality?: string | null
  district: string | null
  lat: Num
  lng: Num
  geo_precision: GeoPrecision
  total_kg: Num
  total_m3: Num
  parcels: number | null
  load_class: string | null
  needs_tail_lift: boolean
  delivery_kind: string | null
  carrier_service_id: string | null
  delivery_cost: Num
  /** Transportadora escolhida à mão. */
  carrier_manual?: boolean | null
  /** Despacho manual: destino escolhido no mapa (`null` = por atribuir). */
  dispatch_kind?: "fleet" | "carrier" | null
  /** Veículo preferido (frota); o dia escolhe-se em «Planear o dia». */
  dispatch_vehicle_id?: string | null
  dispatch_at?: string | null
  /** Dia de recolha (transportadora). */
  pickup_day?: string | null
  /** Aviso ao SAGE (evento → Fluxo → n8n). */
  sage_status?: "sent" | "no_flow" | "failed" | null
  sage_synced_at?: string | null
  sage_error?: string | null
  best_alternative: CarrierOption | null
  options: CarrierOption[]
  block_reason: BlockReason | null
  /** Alguma linha sem peso levou o peso por omissão (`cargo.missing_weight_kg`). */
  weight_assumed?: boolean | null
  confirmed: boolean
  changed_after_confirm: boolean
  manual: boolean
  imported_at: string
}

export interface OrderLine {
  id: string
  order_id: string
  sku: string | null
  description: string | null
  qty: Num
  unit_kg: Num
  unit_m3: Num
  load_class: string | null
  weight_source?: string | null
}

export interface ImportStats {
  total_ready: number
  new: number
  changed: number
  changed_after_confirm: number
  unchanged: number
  cancelled: number
  ignored: number
  without_location: number
  without_weight: number
  blocked: number
  withdrawn?: number
}

export interface ImportResult {
  import_id: string
  stats: ImportStats
  next_offset: number | null
  remaining: number
  warnings: string[]
  errors: string[]
}

export interface StoreSyncResult {
  import_id: string
  stats: ImportStats
  next_cursor: string | null
  read: number
  synced_since: string | null
  warnings: string[]
  errors: string[]
}

export interface OrderImport {
  id: string
  file_key: string
  file_name: string | null
  imported_at: string
  stats: ImportStats
}

export interface Service {
  id: string
  carrier_id: string
  code: string
  name: string
  active: boolean
  zones: string[]
  transit_days: Record<string, number> | number
  pickup_cutoff: string | null
  accepted_classes: string[]
  max_parcel_kg: Num
  max_length_cm: Num
  max_weight_kg: Num
  class_prices: Record<string, Record<string, number>>
  other_rules: Array<{ rule_type: string; zone: string | null; from_value: number; to_value: number | null; unit_price: number }>
}

export interface Carrier {
  id: string
  code: string
  name: string
  kind: "external" | "own_fleet"
  description: string | null
  active: boolean
  services: Service[]
}

export interface Vehicle {
  id: string
  name: string
  kind: string | null
  max_kg: Num
  max_m3: Num
  has_tail_lift: boolean
  cost_per_km: Num
  toll_rate_per_motorway_km: Num
  cost_per_hour_extra: Num
  cost_fixed_day: Num
  shift_start: string
  shift_end: string
  max_drive_min: number | null
  routing_profile: string
  /** Base do veículo (id de `warehouses`); `null` = o único armazém ativo. */
  warehouse_id?: string | null
  active: boolean
}

export interface Warehouse {
  id: string
  code: string
  name: string
  lat: Num
  lng: Num
  address: Record<string, unknown>
  active: boolean
}

export type CargoSource = "validated" | "datasheet" | "category_avg" | "catalog" | "unknown"

export interface ProductRow {
  product_id: string
  sku: string
  name: string | null
  weight_kg: Num
  length_cm: Num
  width_cm: Num
  height_cm: Num
  volume_m3: Num
  load_class: string | null
  needs_tail_lift: boolean
  source: CargoSource
  confidence: Num
  dims_basis: "packaged" | "product" | null
  evidence: {
    status?: "pending" | "done" | "none"
    datasheet_url?: string | null
    model_reference?: string | null
    quotes?: Array<{ field: string; quote: string }>
    tags?: string[]
    needs_review?: boolean
    reason?: string
    rejected?: string
    category_avg?: { n: number; peers: string[] }
    validated_from?: string
    catalog?: {
      weight_source: string | null
      weight_confidence: number | null
      dims_source: string | null
      dims_confidence: number | null
      estimate_status: string | null
    }
  }
  validated_by: string | null
  validated_at: string | null
}

export interface ProductItem {
  sku: string
  name: string | null
  lines: number
  qty: number
  orders: number
  blocked_orders: number
  row: ProductRow | null
  catalog: CatalogLogistics | null
}

/** O que o Koortex sabe do SKU (mestre do host), com origem e confiança. */
export interface CatalogLogistics {
  weight_kg: Num
  weight_source: string | null
  weight_confidence: Num
  length_cm: Num
  width_cm: Num
  height_cm: Num
  dims_basis: string | null
  shape: string | null
  dims_source: string | null
  dims_confidence: Num
  estimate_status: "queued" | "done" | "skipped" | "failed" | null
}

export interface ProductStats {
  skus: number
  lines: number
  validated_lines: number
  validated_lines_pct: number
  validated: number
  datasheet: number
  category_avg: number
  catalog: number
  unknown: number
  missing_dims: number
  estimating: number
  batch_acceptable: number
}

export interface PostalCode {
  id: string
  cp7: string | null
  cp4: string
  lat: Num
  lng: Num
  locality: string | null
  district: string | null
}

export interface QuoteEvaluation {
  zone: string | null
  in_fleet_area: boolean
  due_date: string | null
  load: { total_kg: number; total_m3: number; parcels: number; load_class: string | null; needs_tail_lift: boolean; unknown_skus: string[] }
  options: CarrierOption[]
  best: CarrierOption | null
  carrier_deadline: string | null
  block_reason: BlockReason | null
}

type Res<T> = { success: true; data: T; message?: string } | { success: false; error: string; details?: unknown }

function unwrap<T>(res: Res<T>): T {
  if (!res || res.success === false) throw new Error((res as { error?: string })?.error ?? "Erro desconhecido")
  return res.data
}

const get = async <T,>(s: AppShell, path: string) => unwrap(await s.client.get<Res<T>>(path))
const post = async <T,>(s: AppShell, path: string, body: unknown = {}) => unwrap(await s.client.post<Res<T>>(path, body))
const patch = async <T,>(s: AppShell, path: string, body: unknown) => unwrap(await s.client.patch<Res<T>>(path, body))
const del = async <T,>(s: AppShell, path: string) => unwrap(await s.client.delete<Res<T>>(path))

/** Mensagem legível de um erro do cliente (corpo `{ error }` do host ou da app). */
export function errorMessage(err: unknown): string {
  const body = (err as { body?: { error?: string; message?: string } })?.body
  return body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err))
}

// ─── Ficheiros (rota `files` do host: o proxy REST das funções não passa binários) ───

export async function uploadFile(s: AppShell, file: File): Promise<string> {
  const form = new FormData()
  form.append("file", file, file.name)
  return unwrap(await s.client.post<Res<{ key: string }>>("files", undefined, { body: form })).key
}

// ─── Encomendas ──────────────────────────────────────────────────────────────

export const listOrders = (s: AppShell) => get<{ items: Order[] }>(s, "orders").then((r) => r.items)
export const getOrder = (s: AppShell, id: string) => get<Order & { lines: OrderLine[] }>(s, `orders/${id}`)
export const patchOrder = (s: AppShell, id: string, body: Record<string, unknown>) => patch<Order>(s, `orders/${id}`, body)
export const createOrder = (s: AppShell, body: Record<string, unknown>) => post<Order>(s, "orders", body)
export const importOrdersBatch = (s: AppShell, body: { file_key: string; file_name?: string; import_id?: string; offset?: number }) =>
  post<ImportResult>(s, "orders/import", body)
export const syncStoreBatch = (s: AppShell, body: { import_id?: string; cursor?: string; full?: boolean }) =>
  post<StoreSyncResult>(s, "orders/sync-store", body)
export const listImports = (s: AppShell) => get<{ items: OrderImport[] }>(s, "orders/import").then((r) => r.items)
export const requoteBatch = (s: AppShell, offset: number, relocate = false) =>
  post<{ done: number; total: number; next_offset: number | null }>(s, "orders/requote", { offset, relocate })

/** Recalcula todas as encomendas em aberto (lotes de 60). */
export async function requoteAll(s: AppShell, relocate = false, onProgress?: (done: number, total: number) => void): Promise<number> {
  let offset: number | null = 0
  let total = 0
  while (offset !== null) {
    // Um lote que falhe (rede, proxy) repete-se uma vez antes de desistir.
    const at: number = offset
    const r: { done: number; total: number; next_offset: number | null } = await requoteBatch(s, at, relocate).catch(() => requoteBatch(s, at, relocate))
    total = r.total
    onProgress?.(r.done, r.total)
    offset = r.next_offset
  }
  return total
}

// ─── Transportadoras, frota, armazém ─────────────────────────────────────────

export const listCarriers = (s: AppShell) => get<{ items: Carrier[] }>(s, "carriers").then((r) => r.items)
export const createCarrier = (s: AppShell, body: Record<string, unknown>) => post<Carrier>(s, "carriers", body)
export const patchCarrier = (s: AppShell, id: string, body: Record<string, unknown>) => patch<Carrier>(s, `carriers/${id}`, body)
export const deleteCarrier = (s: AppShell, id: string) => del(s, `carriers/${id}`)
export const createService = (s: AppShell, body: Record<string, unknown>) => post<Service>(s, "services", body)
export const patchService = (s: AppShell, id: string, body: Record<string, unknown>) => patch<Service>(s, `services/${id}`, body)
export const deleteService = (s: AppShell, id: string) => del(s, `services/${id}`)
export const putClassPrices = (s: AppShell, id: string, zone: string | null, prices: Record<string, number | null>) =>
  post(s, `services/${id}/class-prices`, { zone, prices })
export const previewPricingImport = (s: AppShell, file_key: string, service_id: string) =>
  post<{ id: string; preview: unknown[]; summary: { rules_parsed: number; errors: string[] } }>(s, "pricing/imports", { file_key, service_id })
export const confirmPricingImport = (s: AppShell, id: string) => post<{ version: number; created: number }>(s, `pricing/imports/${id}/confirm`)

export const listVehicles = (s: AppShell) => get<{ items: Vehicle[] }>(s, "vehicles").then((r) => r.items)
export const saveVehicle = (s: AppShell, id: string | null, body: Record<string, unknown>) =>
  id ? patch<Vehicle>(s, `vehicles/${id}`, body) : post<Vehicle>(s, "vehicles", body)
export const deleteVehicle = (s: AppShell, id: string) => del(s, `vehicles/${id}`)
export const listWarehouses = (s: AppShell) => get<{ items: Warehouse[] }>(s, "warehouses").then((r) => r.items)
export const saveWarehouse = (s: AppShell, id: string | null, body: Record<string, unknown>) =>
  id ? patch<Warehouse>(s, `warehouses/${id}`, body) : post<Warehouse>(s, "warehouses", body)

export const quote = (s: AppShell, body: { postal_code: string; lines: Array<{ sku: string; qty: number }>; due_date?: string }) =>
  post<QuoteEvaluation>(s, "quote", body)

// ─── Produtos ────────────────────────────────────────────────────────────────

export const listProducts = (s: AppShell) => get<{ items: ProductItem[]; stats: ProductStats }>(s, "products")
export const patchProduct = (s: AppShell, body: Record<string, unknown>) => patch<ProductRow>(s, "products", body)
export const acceptDatasheets = (s: AppShell) => post<{ accepted: string[] }>(s, "products/accept-datasheets")
export const estimateProduct = (s: AppShell, body: { sku: string; datasheet_url?: string; file_key?: string }) =>
  post<{ sku: string; status: string; message: string }>(s, "products/estimate", body)
export const requestDimsEstimates = (s: AppShell, skus?: string[]) => post<{ queued: number }>(s, "products/estimate-dims", skus ? { skus } : {})
export const refreshProducts = (s: AppShell) => post<{ items: Array<{ sku: string; status: string; message: string }> }>(s, "products/refresh")

// ─── Códigos postais ─────────────────────────────────────────────────────────

export interface PostalList {
  items: PostalCode[]
  next_offset: number | null
  by_precision: Record<string, number>
  unmatched: Array<{ postal_code: string; orders: number; precision: GeoPrecision; locality: string | null }>
}
export const listPostal = (s: AppShell, cp4?: string) => get<PostalList>(s, `postal-codes${cp4 ? `?cp4=${encodeURIComponent(cp4)}` : ""}`)
export const importPostalBatch = (s: AppShell, file_key: string, offset: number) =>
  post<{ inserted: number; updated: number; total: number; done: number; next_offset: number | null; rejected: number; errors: string[] }>(
    s,
    "postal-codes/import",
    { file_key, offset },
  )
export const seedExamplePostal = (s: AppShell) => post<{ inserted: number; updated: number }>(s, "postal-codes/example")

// ─── Settings (rota `config` do host; gravar exige administrador) ────────────

export const getConfig = (s: AppShell) =>
  get<{ config: Record<string, unknown>; schema: Record<string, { default?: unknown; description?: string; type?: string }>; readonly: boolean }>(s, "config")
export const patchConfig = (s: AppShell, values: Record<string, unknown>) => patch<{ config: Record<string, unknown> }>(s, "config", { patch: values })

// ─── Proposta da frota (F1) ──────────────────────────────────────────────────

export interface PlanTotals {
  fleet_variable: number
  fleet_fixed: number
  carrier: number
  all_carrier: number
}

export interface PlanDiff {
  compared: number
  changed_day: number
  changed_vehicle: number
  to_carrier: number
  to_fleet: number
  new_orders: number
}

export interface Plan {
  id: string
  status: string
  days: string[]
  carrier: string[]
  unassigned: string[]
  totals: PlanTotals
  stats: { solve_ms?: number; matrix_ms?: number; status?: string; orders?: number; locked?: number; mode?: "manual" }
  diff: PlanDiff | null
  order_count: number
  created_at?: string
}

export interface PlanRoute {
  id: string
  plan_id: string
  vehicle_id: string
  day: string
  status: "draft" | "confirmed"
  stop_count: number
  km: Num
  toll_km: Num
  drive_min: number | null
  start_min: number | null
  end_min: number | null
  kg: Num
  m3: Num
  cost_km: Num
  cost_toll: Num
  cost_overtime: Num
  cost_variable: Num
  cost_fixed: Num
  problems: string[]
}

export interface PlanStop {
  id: string
  route_id: string
  order_id: string
  seq: number
  arrival_min: number | null
  departure_min: number | null
  fleet_cost: Num
  pinned: boolean
  result: "delivered" | "failed" | null
  result_reason: string | null
}

export interface PlanView {
  plan: Plan | null
  routes: PlanRoute[]
  stops: PlanStop[]
  diff?: PlanDiff | null
  /** Hoje (dia útil) e o horizonte: os dias a que se pode atribuir à mão. */
  assignable_days?: string[]
  /** Edições à mão por desfazer. */
  undo_count?: number
}

/** Veículo × dia, a transportadora mais barata no prazo, ou uma transportadora concreta (fica escolhida à mão). */
export type MoveTarget = { vehicle_id: string; day: string } | "carrier" | { carrier_service_id: string } | "unplanned"

export interface RouteMetrics {
  stops: number
  km: number
  end_min: number | null
  cost_variable: number
  cost_fixed: number
}

export interface MoveImpact {
  order_id: string
  from: MoveTarget | "unassigned" | null
  to: MoveTarget
  routes: Array<{ route_id: string | null; vehicle_id: string; day: string; before: RouteMetrics | null; after: RouteMetrics & { problems: string[] } }>
  delta_variable: number
  delta_carrier: number
  delta_fixed: number
  problems: string[]
}

export const moveOrder = (s: AppShell, order_id: string, to: MoveTarget, preview: boolean) =>
  post<{ impact: MoveImpact; applied: boolean }>(s, "planning/move", { order_id, to, preview })
export const undoEdit = (s: AppShell) => post<{ summary: string | null }>(s, "planning/undo")
export const pinOrder = (s: AppShell, order_id: string, pinned: boolean) => post(s, "planning/pin", { order_id, pinned })
export const recordResult = (s: AppShell, order_id: string, result: "delivered" | "failed" | "clear", reason?: string) =>
  post(s, "planning/result", { order_id, result, reason })
export const confirmDay = (s: AppShell, day: string, reopen = false) => post<{ routes: number }>(s, "planning/confirm", { day, reopen })
/** Link do motorista de uma rota confirmada (F2): URL do portal com o token no fragmento. */
export const driverLink = (s: AppShell, route_id: string, renew = false) =>
  post<{ route_id: string; token: string; url: string | null; expires_at: string }>(s, "planning/driver-link", { route_id, renew })

export const getPlan = (s: AppShell) => get<PlanView>(s, "planning")

// ─── Despacho manual ─────────────────────────────────────────────────────────

export type DispatchTarget = { kind: "carrier"; service_id: string } | { kind: "fleet"; vehicle_id?: string | null } | { kind: "none" } | "best_carrier"
export interface DispatchResult {
  order_id: string
  source_ref: string
  dispatch_kind: "fleet" | "carrier" | null
  sage_status: "sent" | "no_flow" | "failed"
  sage_error: string | null
}
export interface DispatchBatch { done: DispatchResult[]; failed: Array<{ order_id: string; error: string }> }

/** Atribui em lotes de 20 (o proxy REST corta aos 30 s); `onProgress(feitas, total)`. */
export async function dispatchOrders(s: AppShell, ids: string[], target: DispatchTarget, onProgress?: (done: number, total: number) => void): Promise<DispatchBatch> {
  const out: DispatchBatch = { done: [], failed: [] }
  for (let i = 0; i < ids.length; i += 20) {
    const r = await post<DispatchBatch>(s, "dispatch", { order_ids: ids.slice(i, i + 20), target })
    out.done.push(...r.done)
    out.failed.push(...r.failed)
    onProgress?.(Math.min(i + 20, ids.length), ids.length)
  }
  return out
}
export const resendSage = (s: AppShell, order_id: string) => post<DispatchResult>(s, "dispatch/resend", { order_id })
export const scheduleDay = (s: AppShell, body: { day: string; vehicle_id: string; add?: string[]; remove?: string[]; optimize?: boolean }) =>
  post<{ route_id: string | null; stops: number; problems: string[] }>(s, "planning/day", body)
export const setPickupDay = (s: AppShell, order_ids: string[], day: string | null) => post<{ updated: number }>(s, "planning/pickup", { order_ids, day })
export const proposePlan = (s: AppShell) => post<PlanView>(s, "planning/propose")
/** Garante um plano para atribuir à mão (sem proposta, cria um vazio). */
export const ensureManualPlan = (s: AppShell) => post<PlanView>(s, "planning/manual")
export const getRouteGeometry = (s: AppShell, routeId: string) => get<{ route_id: string; geometry: string | null }>(s, `planning/geometry?route_id=${encodeURIComponent(routeId)}`)
export const getGoogleMapsConfig = (s: AppShell) => get<{ enabled: boolean; apiKey: string | null }>(s, "google-maps-config")

export type DocKind = "route_sheet" | "load_list" | "labels" | "shipping_list"

/** Gera o documento na Function e descarrega-o (o proxy REST só passa JSON: vem em base64). */
export async function downloadDocument(s: AppShell, kind: DocKind, opts: { route_id?: string; carrier_id?: string; day?: string } = {}): Promise<string> {
  const doc = await post<{ filename: string; content_type: string; content_base64: string }>(s, "planning/documents", { kind, ...opts })
  const bin = atob(doc.content_base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  // Object URL same-origin: o atributo `download` só vale assim.
  const url = URL.createObjectURL(new Blob([bytes], { type: doc.content_type }))
  const a = document.createElement("a")
  a.href = url
  a.download = doc.filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
  return doc.filename
}
